import DOMPurify from 'dompurify'

/**
 * Format the ark parameter into a relative ark, whether the passed in parameter is a full or relative ark, with or without leading slashes.
 * @param arkParam - ark parameter that can be a relative ark like /ark:/61903/1:1:JK48-J27 or full ark like https://familysearch.org/ark:/61903/1:1:JK48-J27, with or without leading slashes
 * @returns relative ark that matches format '/ark:/61903/1:1:JK48-J27' or original arkParam if nothing matches the regex
 */
export function getRelativeArk(arkParam?: string): string | undefined {
  // Find the stuff after the last slash ex. 1:1:JK48-J27
  const arkMatches = arkParam ? arkParam.match(/.*\/([^/]+)$/i) : []
  return arkMatches?.[1] ? `/ark:/61903/${arkMatches[1]}` : arkParam
}

/**
 * Get the ID part of the idParam and trim off any additional stuff such as 4:1 ark formatting. This formats the id parameter to support loading with 4:1 arks as an id parameter.
 * @param idParam - id parameter that can be a FamilyTree person id like 'GX7Q-2Y1' or a tree ark like ark:/61903/4:1:M57L-WNJ
 * @returns FamilyTree person id that matches format 'GX7Q-2Y1' or undefined if no valid ID is found
 */
export function getFamilyTreeId(idParam?: string): string | undefined {
  if (!idParam) return undefined

  // Standard FamilySearch tree ID format is 4 chars + hyphen + 3 chars (like GX7Q-2Y1)
  // But there are also other valid formats, so we'll be more permissive while ensuring basic validity
  const treeIdMatches = idParam.match(/[A-Z0-9]{2,}-[A-Z0-9]{2,}$/i)
  return treeIdMatches?.[0] || undefined
}

/**
 * Sanitize a single query string value before handing it to @fs/zion-linker. qs can parse a value into a string, an
 * array, or a nested object (e.g. `?id[]=x`); the linker only expects plain strings, so anything else is treated as absent.
 * @param value - a raw value from the parsed query string
 * @returns the sanitized string, or an empty string when the value is missing or not a string
 */
export function sanitizeQueryParam(value: unknown): string {
  if (typeof value !== 'string') return ''
  return DOMPurify.sanitize(value)
}
